import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { tutorialArticles } from '../data/articles';

const TutorialDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = tutorialArticles.find((item) => item.slug === slug);

  if (!article) {
    return (
      <section className="min-h-[60vh] bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Tutorial not found</h1>
          <Link to="/tutorials" className="text-red-600 font-medium hover:underline">
            Back to tutorials
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} - ClipThis Tutorials`}
        description={article.summary}
        url={`/tutorials/${article.slug}`}
        schemaData={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: article.title,
          description: article.summary,
          totalTime: article.readingTime,
          step: article.sections.map((section, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: section.heading,
            text: section.body,
          })),
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Tutorials', path: '/tutorials' },
          { name: article.title, path: `/tutorials/${article.slug}` },
        ]}
      />
      <div className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
        <section className="relative z-10 max-w-3xl mx-auto px-6 py-16 animate-fade-up">
          <div className="text-sm text-gray-500 uppercase tracking-[0.3em] mb-2">{article.published}</div>
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-3">{article.title}</h1>
          <p className="text-lg text-gray-600 mb-6">{article.summary}</p>
          <div className="text-sm text-gray-500 mb-10">{article.readingTime}</div>
          <div className="space-y-8">
            {article.sections.map((section) => (
              <div key={section.heading}>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">{section.heading}</h2>
                <p className="text-gray-600 leading-relaxed">{section.body}</p>
              </div>
            ))}
          </div>
          {article.tips && (
            <div className="mt-12 p-6 rounded-2xl border border-gray-200 bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">ClipThis Tips</h3>
              <ul className="space-y-2 text-gray-700">
                {article.tips.map((tip) => (
                  <li key={tip}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-10">
            <Link to="/tutorials" className="text-red-600 font-medium hover:underline">
              ← Back to tutorials
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default TutorialDetail;

